import React from 'react';
import {
  X,
  Loader2,
  BookOpen,
  GraduationCap,
  GitBranch,
} from 'lucide-react';
import type { FiliereWithHierarchy, WriteGuard } from '@/types/filiere.types';
import SemesterCard from './SemesterCard';

interface ViewFiliereModalProps {
  filiere: FiliereWithHierarchy | null;
  isLoading: boolean;
  isOption?: boolean;
  parentFiliereName?: string;
  canManage?: boolean;
  /** Wraps every write so the page can show a campus-scope warning first */
  guardWrite?: WriteGuard;
  onClose: () => void;
  onUpdate: () => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

const ViewFiliereModal: React.FC<ViewFiliereModalProps> = ({
  filiere,
  isLoading,
  isOption = false,
  parentFiliereName,
  canManage = true,
  guardWrite,
  onClose,
  onUpdate,
  onError,
  onSuccess,
}) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className={`px-6 py-4 border-b border-gray-200 flex items-center justify-between ${
            isOption ? 'bg-gradient-to-r from-purple-50 to-purple-100' : 'bg-gradient-to-r from-primary-50 to-blue-50'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isOption ? 'bg-purple-100' : 'bg-primary-100'
              }`}>
                {isOption ? (
                  <GitBranch className="w-5 h-5 text-purple-600" />
                ) : (
                  <GraduationCap className="w-5 h-5 text-primary-600" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isLoading ? 'Chargement...' : filiere?.filiere || 'Détails de la filière'}
                </h2>
                {isOption && parentFiliereName && (
                  <p className="text-sm text-purple-600">Option de {parentFiliereName}</p>
                )}
                {!isLoading && filiere && (
                  <p className="text-sm text-gray-500">
                    {filiere.semesters.length} semestre{filiere.semesters.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className={`w-8 h-8 animate-spin ${isOption ? 'text-purple-600' : 'text-primary-600'}`} />
              </div>
            ) : filiere ? (
              <div className="space-y-4">
                {/* Filiere Info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Code</div>
                    <div className="font-mono text-sm font-medium text-gray-900">{filiere.codeFiliere}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Diplôme</div>
                    <div className="text-sm font-medium text-gray-900">{filiere.diplome}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Durée</div>
                    <div className="text-sm font-medium text-gray-900">{filiere.nombreAnnees} ans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Accréditation</div>
                    <div className="text-sm font-medium text-gray-900">{filiere.anneeAccreditation}</div>
                  </div>
                </div>

                {/* Semesters Section Header */}
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className={`w-5 h-5 ${isOption ? 'text-purple-500' : 'text-gray-500'}`} />
                  <h3 className="font-semibold text-gray-900">
                    Semestres et Modules ({filiere.semesters.length})
                  </h3>
                </div>

                {/* Semesters */}
                {filiere.semesters.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">Aucun semestre créé</p>
                    <p className="text-sm mt-1">
                      {isOption
                        ? 'Aucun semestre créé pour cette option'
                        : 'Aucun semestre créé pour cette filière'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filiere.semesters.map((semester) => (
                      <SemesterCard
                        key={semester.id}
                        semester={semester}
                        canManage={canManage}
                        guardWrite={guardWrite}
                        onUpdate={onUpdate}
                        onError={onError}
                        onSuccess={onSuccess}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Erreur lors du chargement des détails</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewFiliereModal;
